import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

export function WebsiteSecurityEmailDeliverabilityGuide() {
  return (
    <div>
      <SEO
        title="Why Website Security Affects Email Deliverability"
        description="Learn how your website's security impacts whether your emails reach the inbox. Enterprise email gateways check domain reputation including SSL certificates, security headers, and blacklist status."
        canonical="/guides/website-security-email-deliverability"
        ogType="article"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-teal-50/50 to-transparent py-16 dark:from-teal-950/20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to Help Center
          </Link>
          <h1 className="mt-8 text-4xl font-bold tracking-tight sm:text-5xl">
            Why Website Security Affects Email Deliverability
          </h1>
          <p className="mt-6 text-xl text-muted-foreground leading-relaxed">
            Enterprise email security systems don't just look at your email authentication — they check your entire domain reputation, including your website's security posture.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">

          {/* Section 1 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              The Hidden Connection Between Website and Email Security
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                You've set up SPF, DKIM, and DMARC correctly. Your email authentication is perfect.
                But some of your emails are still being flagged or quarantined by enterprise recipients.
                <strong className="text-foreground"> What's going on?</strong>
              </p>
              <p>
                Enterprise email security gateways like Proofpoint, Mimecast, Barracuda, and Microsoft
                Defender don't just verify your email authentication. They perform comprehensive domain
                reputation checks that include your website's security.
              </p>
              <p>
                These systems ask: <strong className="text-foreground">"Is this domain professionally
                maintained, or does it look neglected and potentially compromised?"</strong>
              </p>
            </div>

            <div className="mt-8 rounded-2xl border-l-4 border-teal-500 bg-teal-50 p-6 dark:bg-teal-950/30">
              <p className="text-lg font-medium text-teal-800 dark:text-teal-200">
                A domain with poor website security signals to email gateways that it may be compromised,
                abandoned, or poorly managed — all red flags that affect email deliverability.
              </p>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 2 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              What Email Security Systems Check
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                When a corporate email gateway receives an email from your domain, it may perform
                several background checks beyond just SPF, DKIM, and DMARC:
              </p>
            </div>

            <div className="mt-10 space-y-6">
              <div className="rounded-2xl border bg-card p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 text-teal-600">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">SSL/TLS Certificate</h3>
                    <p className="mt-2 text-muted-foreground">
                      Is your website using HTTPS? Is the certificate valid, not expired, and issued
                      by a trusted authority? An expired or missing SSL certificate is a major red flag.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border bg-card p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 text-teal-600">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Security Headers</h3>
                    <p className="mt-2 text-muted-foreground">
                      Does your website implement modern security headers like HSTS, Content Security
                      Policy, and X-Frame-Options? These indicate a security-conscious organization.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border bg-card p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 text-teal-600">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Blacklist Status</h3>
                    <p className="mt-2 text-muted-foreground">
                      Is your domain or IP address listed on any DNS-based blacklists (DNSBLs)?
                      Being on even one blacklist can severely impact email delivery.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border bg-card p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 text-teal-600">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Domain Age and History</h3>
                    <p className="mt-2 text-muted-foreground">
                      Newly registered domains or domains with a history of spam/phishing are
                      treated with more suspicion. A strong security posture helps build trust.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 3 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              How Poor Website Security Hurts Email Delivery
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                Here's a real-world scenario: You're a sales rep at a B2B company sending proposals
                to enterprise prospects. Your email authentication is perfect, but your website:
              </p>
            </div>

            <ul className="mt-6 space-y-3 text-lg text-muted-foreground">
              <li className="flex items-start gap-3">
                <svg className="mt-1 h-5 w-5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Has an SSL certificate expiring in 3 days</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="mt-1 h-5 w-5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Is missing HSTS and Content Security Policy headers</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="mt-1 h-5 w-5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Uses outdated TLS 1.0 protocol</span>
              </li>
            </ul>

            <div className="mt-8 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                When your email arrives at a company using Proofpoint or Mimecast, their system
                might check your domain and see these red flags. Result? Your email gets flagged
                for additional scrutiny or quarantined, even with perfect DMARC alignment.
              </p>
              <p>
                This is especially common when sending to:
              </p>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border bg-muted/30 p-4">
                <span className="font-medium">Financial services</span>
                <p className="mt-1 text-sm text-muted-foreground">Banks, insurance, fintech</p>
              </div>
              <div className="rounded-xl border bg-muted/30 p-4">
                <span className="font-medium">Healthcare</span>
                <p className="mt-1 text-sm text-muted-foreground">Hospitals, health tech, pharma</p>
              </div>
              <div className="rounded-xl border bg-muted/30 p-4">
                <span className="font-medium">Government</span>
                <p className="mt-1 text-sm text-muted-foreground">Federal, state, local agencies</p>
              </div>
              <div className="rounded-xl border bg-muted/30 p-4">
                <span className="font-medium">Large enterprises</span>
                <p className="mt-1 text-sm text-muted-foreground">Fortune 500 companies</p>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 4 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              How to Improve Your Website Security Score
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                The good news is that most website security issues are easy to fix. Here's your
                checklist:
              </p>
            </div>

            <div className="mt-8 space-y-6">
              <div className="rounded-xl border-l-4 border-teal-500 bg-card p-6">
                <h3 className="font-semibold text-lg">1. SSL/TLS Certificate</h3>
                <ul className="mt-3 space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Use HTTPS on all pages (not just checkout)
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Enable TLS 1.2 or 1.3 (disable TLS 1.0/1.1)
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Set up auto-renewal for certificates
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Monitor certificate expiration dates
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border-l-4 border-teal-500 bg-card p-6">
                <h3 className="font-semibold text-lg">2. Security Headers</h3>
                <ul className="mt-3 space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Enable Strict-Transport-Security (HSTS)
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Add Content-Security-Policy header
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Set X-Frame-Options to DENY or SAMEORIGIN
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Add X-Content-Type-Options: nosniff
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border-l-4 border-teal-500 bg-card p-6">
                <h3 className="font-semibold text-lg">3. Blacklist Monitoring</h3>
                <ul className="mt-3 space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Regularly check major blacklists (Spamhaus, SpamCop, Barracuda)
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Set up alerts for new blacklist entries
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Act quickly if listed (each blacklist has a removal process)
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* CTA Section */}
          <section className="rounded-2xl bg-gradient-to-br from-teal-50 to-cyan-50 p-8 dark:from-teal-950/30 dark:to-cyan-950/30">
            <h2 className="text-2xl font-bold">Check Your Website Security Now</h2>
            <p className="mt-4 text-muted-foreground">
              Use our free Website Security Scanner to check your SSL certificate, security headers,
              and blacklist status. Get a comprehensive report with specific recommendations.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/tools"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-teal-600 px-6 py-3 font-semibold text-white shadow-lg shadow-teal-500/25 transition-all hover:bg-teal-700 hover:shadow-xl"
              >
                Scan Your Website
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <Link
                to="/guides/fixing-security-headers"
                className="inline-flex items-center justify-center gap-2 rounded-full border bg-background px-6 py-3 font-semibold transition-colors hover:bg-muted"
              >
                How to Fix Security Headers
              </Link>
            </div>
          </section>

        </div>
      </article>
    </div>
  );
}
